import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { AnswerItem, QuestionValidationTypes, ReduxState, QuestionItems } from 'interface';
import { QuestionSLPRef, SLPQuestionType } from '../../questions.index';
import SLPMatrix, { SLPMatrixRef, MatrixValue } from 'utilities/slp/matrix/matrix.index';
import { createQuestionTitle } from '../questionsTypes.script';
import { extractTitle } from 'scripts/string';

const SLPMatrixQuestion: React.ForwardRefRenderFunction<
    QuestionSLPRef,
    ConnectedProps<typeof connector> & SLPQuestionType
> = (props: ConnectedProps<typeof connector> & SLPQuestionType, ref) => {
    React.useImperativeHandle(ref, () => ({
        validate: (e?: boolean) => validate(e),
        submit: (e?: boolean) => onSubmit(e),
    }));
    const lang = props.lang.SLP.questionsTypes;
    const question = props.question;
    const inputRef = useRef<SLPMatrixRef>(null);
    const multi = !!question.validations?.find((i) => i.title === QuestionValidationTypes.matrixMultiSelect)?.value;
    const items: MatrixValue[] = [];
    question.children
        ?.sort((a, b) => (a.order ? a.order : a.id) - (b.order ? b.order : b.id))
        .map((i) => {
            const columns = i.items?.map((j) => ({
                title: j.title,
                payload: j.id,
                order: j.order,
            }));
            if (columns) items.push({ row: { title: i.title, payload: i.id, order: i.order }, columns });
        });
    const [title, setTitle] = useState(question.title);

    const [hint, setHint] = useState(question.description);

    useEffect(() => {
        if (question.description)
            setHint(
                createQuestionTitle(
                    question.description,
                    props.answerList,
                    props.answer?.answerer_info,
                    props.form.indicators,
                ),
            );
        setTitle(
            createQuestionTitle(question.title, props.answerList, props.answer?.answerer_info, props.form.indicators),
        );
    }, [props.answerList]);

    function validate(e?: boolean) {
        const value = inputRef.current?.getValue();
        if (
            question.is_required &&
            value &&
            props.question.children &&
            value?.length < props.question.children?.length
        ) {
            if (e) inputRef.current?.setError(multi ? lang.multiSelectEmp : lang.selectiveErr);
            return false;
        }
        return true;
    }

    function setAnswer() {
        const values = inputRef.current?.getValue();
        let data = [...props.answerList];
        data = [...data.filter((item) => !props.question.children?.map((item) => item.id).includes(item.question.id))];

        if (values?.length && validate()) {
            for (let i = 0; i < values.length; i++) {
                const value = values[i];
                const question = props.question.children?.find((item) => item.id === value.row.payload);
                const childIndex = data.findIndex((i) => i.question.id === value.row.payload);
                if (question) {
                    const response: AnswerItem = {
                        question_items: value.columns.map((i) => ({
                            title: i.title,
                            id: i.payload,
                            payload: i.payload,
                        })),
                        question: question,
                        id: new Date().getTime() + i,
                        answer: props.answer?.id,
                    };
                    if (childIndex !== undefined && childIndex > -1) {
                        data[childIndex] = response;
                    } else {
                        data?.push(response);
                    }
                }
            }
        }
        props.setAnswerList(data);
    }

    function onSubmit(e?: boolean) {
        const values = inputRef.current?.getValue();
        if (values?.length && validate(e)) {
            for (let i = 0; i < values.length; i++) {
                const value = values[i];
                const question = props.question.children?.find((item) => item.id === value.row.payload);
                if (question) {
                    props.submit({
                        question_items: value.columns.map((i) => ({
                            id: i.payload,
                            title: i.title,
                            payload: i.payload,
                        })),
                        question: question,
                        id: new Date().getTime(),
                        created_at: new Date().toISOString(),
                    });
                }
            }
        }
    }

    const defaultValue: MatrixValue[] = [];
    if (question.children?.length) {
        question.children.map((child) => {
            const index = props.answerList?.findIndex((i) => i.question.id === child.id);
            if (index !== undefined && index > -1) {
                const items = props.answerList[index].question_items;
                if (items && items.length) {
                    defaultValue.push({
                        row: { title: child.title, payload: child.id },
                        columns: items.map((item) => ({
                            title: item.title,
                            payload: item.id,
                            value: item.value?.toString(),
                        })),
                    });
                }
            }
        });
    }

    return (
        <SLPMatrix
            hint={extractTitle(hint, false, false)}
            ref={inputRef}
            counter={props.counter}
            label={extractTitle(title, false, false)}
            image={question.image_file}
            items={items}
            multiSelect={multi}
            color={props.form?.answer_color}
            labelColor={props.form?.question_color}
            required={question.is_required}
            onChange={setAnswer}
            dir={props.form.direction}
            defaultValue={defaultValue}
            id={props.question.id.toString()}
        />
    );
};
const mapStateToProps = (state: ReduxState) => ({
    direction: state.direction,
    lang: state.lang,
});

const connector = connect(mapStateToProps, null, null, { forwardRef: true });
export default connector(React.forwardRef(SLPMatrixQuestion));
